import React, {useEffect, useState} from "react";
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PropTypes from "prop-types";

/**
 * Confirmation dialog for destructive actions.
 *
 * `onDelete` may return a Promise — while it's pending we disable
 * both buttons and show a spinner on the Delete button so the user
 * gets feedback and can't double-click into duplicate deletes.
 */
function DeleteConfirmDialog({open, onClose, onDelete, warningMessage}) {
    const [isDeleting, setIsDeleting] = useState(false);

    // Reset the busy flag whenever the dialog reopens. Belt-and-braces
    // in case onDelete throws synchronously and we never reach finally.
    useEffect(() => {
        if (open) setIsDeleting(false);
    }, [open]);

    const handleDelete = async () => {
        if (isDeleting) return;
        try {
            setIsDeleting(true);
            await onDelete();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isDeleting ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Delete</DialogTitle>
            <DialogContent>
                <SoftTypography>{warningMessage}</SoftTypography>
            </DialogContent>
            <DialogActions>
                <SoftButton onClick={onClose} disabled={isDeleting} variant="outlined">
                    Cancel
                </SoftButton>
                <SoftButton
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={16} color="inherit"/> : null}
                    sx={{minWidth: 110}}
                >
                    {isDeleting ? "Deleting…" : "Delete"}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
}

DeleteConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    warningMessage: PropTypes.string.isRequired,
};

export default DeleteConfirmDialog;
